import { useEffect, useState } from 'react';

import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

/**
 * `useThemeObserver`, but silent until the component has mounted.
 *
 * atlassian.design serves one prerendered HTML to every reader, and inline scripts set
 * `data-color-mode` on `<html>` before first paint. `useThemeObserver` reads that attribute in its
 * `useState` initialiser, so it returns `{}` on the server and the live mode during the first client
 * render. A component that branches its markup on it therefore hydrates differently from the HTML
 * for dark readers, and React 18 leaves mismatched attributes as the server rendered them.
 *
 * This hook returns `{}` until after mount, so the first client render always matches the server,
 * then follows the live theme exactly as `useThemeObserver` does. The cost is a visible swap for dark
 * readers once the effect runs. Prefer resolving the difference in CSS with a
 * `html[data-color-mode="dark"] &` selector, which has no such cost; use this hook only where CSS
 * cannot carry the difference, such as a `<video src>` or a token value shown as text.
 */
export const useThemeObserverAfterHydration = (): ReturnType<typeof useThemeObserver> => {
	const theme = useThemeObserver();
	const [hasMounted, setHasMounted] = useState(false);

	useEffect(() => {
		setHasMounted(true);
	}, []);

	return hasMounted ? theme : {};
};
